import { get } from 'lodash';
import {
  DATA_TYPE_UOM_CATEGORIES,
  NON_APPLICABLE_UOM
} from '../constants/unitOfMeasure';
import { KeyValuePair, LabelValuePair } from '../interfaces';
import { CodeTableCodeTypeOf, CodeTableNumber } from '../interfaces/codeTables';

const EMPTY_ARRAY: any[] = [];

type ObjectWithIndexableKey = Record<string, any>;

/** Pluck the specified property from each item in the given array. */
export function pluck<T extends object, K extends keyof T>(
  array: Array<T> | null,
  key: K
): T[K][] {
  const PLUCK_EMPTY_ARRAY: Array<T> = [];

  return (array || PLUCK_EMPTY_ARRAY).map((item) => item[key]);
}

/** Type that takes T argument to indicate the type of the multidimensional array. */
export type MultiDimensionalArray<T> = (T | (T | T[])[])[];

/**
 * Sort an array of items **in-place** by performing a _case-insensitive_ comparison
 * of the given property for items. Use the `options` parameter to
 * customize the comparison (e.g. whether uppercase or lowercase should sort first or
 * whether numeric collation should be used).
 *
 * @example <caption>Perform comparison with lowercase coming first:</caption>
 * sortAlphabetically(items, 'name', {caseFirst: 'lower'})
 *
 * @example <caption>Perform comparison numerically (e.g. "1" < "2" < "10"):</caption>
 * sortAlphabetically(items, 'id', {numeric: true})
 */
export function sortAlphabetically<T extends object>(
  array: Array<T>,
  key: keyof T,
  options?: Intl.CollatorOptions
) {
  return array.sort((a, b) => {
    const value = a[key];
    const compareValue = b[key];

    return typeof value === 'string' && typeof compareValue === 'string'
      ? value.localeCompare(compareValue, undefined, options)
      : 0;
  });
}

/**
 * Transform an array of key-value items into an object with the same key-value pairs.
 *
 * @param {Object[]} [items] - Array of items to transform.
 * @param {string} items[].Key - An item's key.
 * @param {*} items[].Value - An item's value.
 *
 * @returns {Object.<string,*>} An object containing the key-value pairs.
 */
export function transformKeyValueArrayToObject<T = string>(
  items?: { [key: string]: any }[],
  keyName: string = 'Key',
  valueName: string = 'Value'
): Record<string, T> {
  if (!items) {
    return {};
  }

  return items.reduce((obj, item) => {
    obj[item[keyName]] = item[valueName];

    return obj;
  }, {});
}

/**
 * Transform an array of objects into an object with Key as an identifier from each item (e.g. `Id`) or
 * the object path as (e.g.: `Id.Value') and Value as item of type T.
 * @param items :list of items of type T
 * @param keyName : Key name/path , can be nested key eg: Id.Value
 * @returns: Map of List of items by specified Key name/Path
 */
export function transformObjectArrayToObject<T extends ObjectWithIndexableKey>(
  items: Array<T> | null,
  keyName: keyof T | string
): Record<string, T> {
  const transformedItems: Record<string, T> = {};

  if (!items) {
    return transformedItems;
  }

  return items.reduce((obj, item) => {
    obj[get(item, keyName)] = item;

    return obj;
  }, transformedItems);
}

export function recordToKeyValuePair(
  src: Record<string, string>
): KeyValuePair<string>[] {
  return Object.keys(src).map((key) => {
    return { Key: key, Value: src[key] };
  });
}

export function groupObjectArrayItemsByKey<T extends Record<string, any>>(
  array: T[],
  itemKey: keyof T
): Record<string, T[]> {
  const result: Record<string, T[]> = {};

  return (array || EMPTY_ARRAY).reduce((itemsByKey, item) => {
    if (!itemsByKey[item[itemKey]]) {
      itemsByKey[item[itemKey]] = [item];
    } else {
      itemsByKey[item[itemKey]].push(item);
    }
    return itemsByKey;
  }, result);
}

export function formatUomArrayItems(
  uomList: CodeTableCodeTypeOf<CodeTableNumber.UnitsOfMeasure>[],
  displayOnlyShortName?: boolean
): LabelValuePair[] {
  return (uomList || EMPTY_ARRAY)
    .filter((uom) => {
      return uom.Value !== NON_APPLICABLE_UOM;
    })
    .sort((curr, next) => {
      return DATA_TYPE_UOM_CATEGORIES.includes(
        curr.AdditionalProperties.category
      )
        ? 1
        : !DATA_TYPE_UOM_CATEGORIES.includes(next.AdditionalProperties.category)
          ? 1
          : -1;
    })
    .map((item) => {
      return {
        label:
          DATA_TYPE_UOM_CATEGORIES.includes(
            item.AdditionalProperties.category
          ) && !displayOnlyShortName
            ? `${item.AdditionalProperties.short_name}\u00A0\u00A0\u00A0\u00A0\u00A0${item.Description}`
            : `${item.AdditionalProperties.short_name}`,
        value: item.Value
      };
    });
}
